import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * 중앙 정렬 Alert 모달
 * alert() 및 confirm() 대신 사용하는 커스텀 모달
 *
 * @param {boolean} isOpen - 모달 표시 여부
 * @param {string} message - 표시할 메시지
 * @param {function} onClose - 닫기/취소 콜백
 * @param {function} onConfirm - 확인 콜백 (confirm 모드일 때)
 * @param {string} type - 'alert' | 'confirm'
 * @param {string} alertType - 'success' | 'warning' | 'error' | 'info' (alert 모드일 때 색상)
 */
function AlertModal({ isOpen, message, onClose, onConfirm, type = 'alert', alertType = 'info' }) {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const isConfirm = type === 'confirm';

  // alertType에 따른 색상 설정
  const getAlertColor = () => {
    switch (alertType) {
      case 'success':
        return '#10b981'; // 초록색
      case 'warning':
        return '#f59e0b'; // 노란색
      case 'error':
        return '#ef4444'; // 빨간색
      case 'info':
      default:
        return '#4f46e5'; // 보라색 (기본)
    }
  };

  const alertColor = getAlertColor();

  return (
    <div style={overlayStyle} onClick={isConfirm ? null : onClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div style={messageStyle}>
          {message}
        </div>

        {isConfirm ? (
          // Confirm 모드: 취소 + 확인 버튼
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              style={cancelButtonStyle}
              onClick={onClose}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#6b7280'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#9ca3af'}
            >
              {t('common.cancel')}
            </button>
            <button
              style={confirmButtonStyle}
              onClick={onConfirm}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#4338ca'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#4f46e5'}
            >
              {t('common.confirm')}
            </button>
          </div>
        ) : (
          // Alert 모드: 확인 버튼만 (타입별 색상 적용)
          <button
            style={{...buttonStyle, backgroundColor: alertColor}}
            onClick={onClose}
            onMouseEnter={(e) => {
              const darkerColor = alertType === 'success' ? '#059669' :
                                  alertType === 'warning' ? '#d97706' :
                                  alertType === 'error' ? '#dc2626' : '#4338ca';
              e.target.style.backgroundColor = darkerColor;
            }}
            onMouseLeave={(e) => e.target.style.backgroundColor = alertColor}
          >
            {t('common.confirm')}
          </button>
        )}
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0, 0, 0, 0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 10000
};

const modalStyle = {
  backgroundColor: 'white',
  borderRadius: '12px',
  padding: '32px',
  maxWidth: '400px',
  width: '90%',
  boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  animation: 'fadeIn 0.2s ease-out'
};

const messageStyle = {
  fontSize: '16px',
  lineHeight: '1.6',
  color: '#1f2937',
  marginBottom: '24px',
  whiteSpace: 'pre-line',
  textAlign: 'center',
  wordBreak: 'break-word',
  overflowWrap: 'break-word'
};

const buttonStyle = {
  backgroundColor: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  width: '100%',
  transition: 'all 0.2s ease'
};

const cancelButtonStyle = {
  backgroundColor: '#9ca3af',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  flex: 1,
  transition: 'all 0.2s ease'
};

const confirmButtonStyle = {
  backgroundColor: '#4f46e5',
  color: 'white',
  border: 'none',
  borderRadius: '8px',
  padding: '12px 24px',
  fontSize: '14px',
  fontWeight: '600',
  cursor: 'pointer',
  flex: 1,
  transition: 'all 0.2s ease'
};

export default AlertModal;
